/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import { css as cssUnbounded, jsx, keyframes } from '@compiled/react';

import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const shimmerAnimation = keyframes({
	'0%': {
		backgroundPosition: '200% 0',
	},
	'100%': {
		backgroundPosition: '-200% 0',
	},
});

const shimmerGradient = cssUnbounded({
	background: `linear-gradient(
		90deg,
		${token('color.text.subtlest')} 0%,
		${token('color.text.subtlest')} 20%,
		#C7CDDC 40%,
		${token('color.text.subtlest')} 60%,
		${token('color.text.subtlest')} 80%,
		#C7CDDC 90%,
		${token('color.text.subtlest')} 100%
	)`,
	backgroundSize: '200% 100%',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	animation: `${shimmerAnimation} 4s linear infinite`,
});

/** Single-line ellipsis + flex/grid-safe width. */
const truncateSingleLine = cssUnbounded({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	minWidth: 0,
	width: '100%',
	maxWidth: '100%',
});

type ShimmerTextProps = {
	as?: 'div' | 'span';
	children: React.ReactNode;
	size?: 'small' | 'medium' | 'large';
	weight?: 'regular' | 'medium' | 'semibold';
	/**
	 * Truncation is applied on this wrapper (not `Text maxLines`) so the
	 * gradient and ellipsis share one box and `white-space` does not break clamping.
	 * @default false
	 */
	truncate?: boolean;
};

export const ShimmerText = ({
	as = 'div',
	children,
	size = 'medium',
	weight = 'regular',
	truncate = false,
}: ShimmerTextProps): JSX.Element => {
	const content = (
		<Text size={size} weight={weight} color="color.text.subtlest">
			{children}
		</Text>
	);

	if (as === 'span') {
		return (
			<span css={[shimmerGradient, truncate ? truncateSingleLine : undefined]}>{content}</span>
		);
	}

	return <div css={[shimmerGradient, truncate ? truncateSingleLine : undefined]}>{content}</div>;
};
